import * as vp from 'vopay';
import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '@/services/api.config';
import { useDataFetchIndicator } from '..';

import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  vp.ApprovalInboxTransactionsResponse | undefined,
  (requestParams: vp.ApprovalInboxTransactionsRequest) => void,
  () => void,
  (requestParams: vp.ApprovalInboxTransactionsRequest) => Promise<vp.ApprovalInboxTransactionsResponse>,
];

function useFetchApprovalTransactionsInbox({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.ApprovalInboxTransactionsRequest>();
  const [response, setResponse] = useState<vp.ApprovalInboxTransactionsResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const { permissions } = usePermissions();
  const {
    response: { TransactionApprovalWorkflowEnabled },
  } = useClientData();

  useEffect(() => {
    if (!permissions.ViewTransactionApprovalWorkflow || !vp.toBool(TransactionApprovalWorkflowEnabled)) {
      return;
    }
    const sendRequest = async (requestParams: vp.ApprovalInboxTransactionsRequest) => {
      setDataFetchingInfo(true);

      const res = await api.fetchApprovalTransactionsInbox(requestParams, source);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.ApprovalInboxTransactionsRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
    async (requestParams: vp.ApprovalInboxTransactionsRequest) => {
      try {
        setDataFetchingInfo(true);
        const res = await api.fetchApprovalTransactionsInbox(requestParams, source);
        setResponse(res);
        return res;
      } finally {
        setDataFetchingInfo(false);
      }
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchApprovalTransactionsInbox);
